// platform/frontend/src/components/plans/ViewPlanDetails.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { getApiBaseURL } from "../../utils/api";
import { formatDate } from "../../utils/dateFormatter";
import { useAuth } from "../../contexts/AuthContext";
import { useAuditedPlanOperations } from "../../hooks/useAuditedPlanOperations";

const ViewPlanDetails = ({ plan, onClose }) => {
  const { tenant_id } = useAuth();
  const { auditPlanView, auditPlanExport } = useAuditedPlanOperations();
  const [pdfError, setPdfError] = useState("");

  useEffect(() => {
    // Audit the plan view when component mounts
    if (plan) {
      auditPlanView(plan);
    }
  }, [plan?.plan_id, auditPlanView]);

  if (!plan) return null;

  const handlePreview = async (plan_id) => {
    const apiBase = getApiBaseURL();
    setPdfError("");

    try {
      // Audit the PDF export
      await auditPlanExport(plan);

      const res = await axios.get(`${apiBase}/api/plans/${plan_id}/pdf`, {
        params: { tenant_id },
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      // Log error silently in production
      setPdfError("Failed to generate PDF preview. Please try again.");
    }
  };

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Plan Details</h5>
        <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
          Close
        </button>
      </div>

      {/* HIPAA Notice for viewing */}
      <div className="card-body">
        <div className="alert alert-info mb-3">
          <small>
            <i className="bi bi-info-circle me-1"></i>
            This view contains Protected Health Information. Access is being
            logged for compliance.
          </small>
        </div>

        <div className="col-md-12">
          <strong>Plan Name:</strong> {plan.plan_name}
        </div>
        <div className="container">
          <div className="row align-items-start">
            <div className="col">
              <strong>Consumer:</strong> {plan.consumer_name}
            </div>
            <div className="col">
              <strong>Date of Plan:</strong>{" "}
              {formatDate(plan.date_of_plan || "—")}
            </div>
          </div>
        </div>

        <div className="container">
          <div className="row align-items-start">
            <div className="col">
              <strong>Initial Plan:</strong> {plan.initial_plan ? "Yes" : "No"}
            </div>
            <div className="col">
              <strong>Updated Plan:</strong> {plan.updated_plan ? "Yes" : "No"}
            </div>
          </div>
        </div>

        <div className="container">
          <div className="row align-items-start">
            <div className="col-md-12">
              <strong>Individuals involved in the development of plan:</strong>
            </div>
            <div className="col-md-12">
              <strong>Name (Consumer/Agency/Title/Family Member/Other):</strong>{" "}
            </div>
            <div className="col-md-12">{plan.participants}</div>
            <div className="col-md-12">
              <strong>Strengths & Resources:</strong>{" "}
              {plan.strengths_and_resources}
            </div>
            <div className="col-md-12">
              <strong>Issues/needs Identified:</strong> {plan.issues_identified}
            </div>
            <div className="col-md-12">
              <strong>Cultural considerations, resources and needs:</strong>{" "}
              {plan.cultural || "—"}
            </div>
            <div className="col-md-12">
              <strong>
                Coordination of Services - (identify other services consumer is
                receiving and explain how they are being coordinated):
              </strong>{" "}
              {plan.coordination || "—"}
            </div>
          </div>
        </div>

        {/* Signature Images */}
        {plan.consumer_signature && (
          <div className="mb-3">
            <label>
              <strong>Consumer Signature:</strong>
            </label>
            <div>
              <img
                src={plan.consumer_signature}
                alt="Consumer Signature"
                style={{
                  border: "1px solid #ccc",
                  maxWidth: 500,
                  maxHeight: 150,
                }}
              />
            </div>
            <strong>Consumer Signature Date:</strong>{" "}
            {formatDate(plan.consumer_signature_date || "—")}
          </div>
        )}
        {plan.crp_signature && (
          <div className="mb-3">
            <label>
              <strong>CRP Signature:</strong>
            </label>
            <div>
              <img
                src={plan.crp_signature}
                alt="CRP Signature"
                style={{
                  border: "1px solid #ccc",
                  maxWidth: 500,
                  maxHeight: 150,
                }}
              />
            </div>
            <strong>CRP Signature Date:</strong>{" "}
            {formatDate(plan.crp_signature_date || "—")}
          </div>
        )}
        {plan.wvdrs_signature && (
          <div className="mb-3">
            <label>
              <strong>WVDRS Signature:</strong>
            </label>
            <div>
              <img
                src={plan.wvdrs_signature}
                alt="WVDRS Signature"
                style={{
                  border: "1px solid #ccc",
                  maxWidth: 500,
                  maxHeight: 150,
                }}
              />
            </div>
            <strong>WVDRS Signature Date:</strong>{" "}
            {formatDate(plan.wvdrs_signature_date || "—")}
          </div>
        )}

        <p>
          <strong>Goals & Objectives:</strong> {plan.goals_and_objectives}
        </p>
        <p>
          <strong>Timeframe:</strong> {plan.timeframe}
        </p>
        <p>
          <strong>Services:</strong>{" "}
          {plan.services?.length
            ? plan.services.map((s) => s.service_name).join(", ")
            : "—"}
        </p>
        <p>
          <strong>Frequency:</strong> {plan.frequency}
        </p>
        <p>
          <strong>Responsible Person:</strong> {plan.responsible_person}
        </p>
        {/* <p>
          <strong>Approval Status:</strong> {plan.approval_status}
        </p>
        <p>
          <strong>Status:</strong> {plan.status}
        </p> */}

        <p></p>
      </div>
      <div className="card-footer">
        {pdfError && (
          <div className="alert alert-danger mb-2" role="alert">
            {pdfError}
          </div>
        )}
        <strong>Download:</strong>{" "}
        <button
          className="btn btn-sm btn-outline-primary ms-2"
          onClick={() => handlePreview(plan.plan_id)}
        >
          Preview Plan
        </button>
        <small className="text-muted ms-2">
          <i className="bi bi-info-circle"></i> PDF export will be logged
        </small>
      </div>
    </div>
  );
};

export default ViewPlanDetails;
